import { zodResolver } from '@hookform/resolvers/zod';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Currency } from '@seapoint/server/enums';
import { ComponentProps, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../design-system/button/button';
import { Form, FormControl, FormField, FormItem, FormLabel } from '../../../../design-system/form/form-primitives';
import { Input } from '../../../../design-system/input/input';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountView } from '../../../../types/entity-types';
import { cn } from '../../../../utils/cn';
import { getBankAccountName } from '../../../../utils/get-bank-account-name';
import { getCurrencySymbol } from '../../../../utils/get-currency-symbol';
import { AccountSelectItem } from '../../components/account-select-item';
import { BeneficiaryCard } from '../../components/beneficiary-card';
import { canInitiatePayments } from '../../utils/capability-checks';
import { CurrencyAccountNotFound } from './currency-account-not-found';

const MODULR_SPECIAL_CHARS = /[-.\s&/,:]*/g;

// See min and max payment amount in Modulr API docs
// https://modulr.readme.io/reference/createpayments
const MINIMUM_PAYMENT_AMOUNT = 0.01;
const MAXIMUM_PAYMENT_AMOUNT = 2147483647;

const createFormSchema = (currency?: Currency, balance?: number) =>
  z.object({
    amount: z.coerce
      .number({ message: 'Payment amount is required' })
      .min(MINIMUM_PAYMENT_AMOUNT, { message: 'Payment amount must be at least 0.01' })
      .max(balance ?? MAXIMUM_PAYMENT_AMOUNT, { message: 'Payment amount must not exceed the available balance' })
      .max(MAXIMUM_PAYMENT_AMOUNT, { message: 'Payment amount must not exceed 2,147,483,647' }),
    sourceAccountId: z.string(),
    paymentReference: z.string().refine(
      // First argument of .refine is the validation function
      // Truthy passes the validation, falsy fails the validation
      // https://zod.dev/?id=refine
      (str) => {
        // Min 6 to max 18 characters that are not all the same (up to 140 characters for currencies other than GBP).
        // https://modulr.readme.io/reference/sendpayment
        const hasAllSameChars = new Set(str).size === 1;

        if (hasAllSameChars) {
          return false;
        }

        // Remove special characters before checking the minimum length requirements
        const strippedStr = str.replace(MODULR_SPECIAL_CHARS, '');

        // Payment reference must consist of at least 6 alphanumeric characters that are not all the same. Optional, uncounted characters include space, hyphen(-), full stop (.), ampersand(&), forward slash (/), comma(,) and colon(:). Total of all characters must be less than 140 for IBAN payment and 18 for SCAN payment.
        // https://modulr.readme.io/reference/sendpayment
        const validCharPattern = /^[a-zA-Z0-9-.&/\s]*$/;

        if (!validCharPattern.test(strippedStr)) {
          return false;
        }

        // Min 6 to max 18 characters that are not all the same (up to 140 characters for currencies other than GBP).
        // At least 6 characters must be alphanumeric and the total length can be up to 140 characters.
        // We check if the stripped string is at least 6 characters and the original string is 140 characters or fewer.
        // https://modulr.readme.io/reference/sendpayment
        return (
          (currency !== Currency.GBP && strippedStr.length >= 6 && str.length <= 140) ||
          (currency === Currency.GBP && strippedStr.length >= 6 && str.length <= 18)
        );
      },
      // Second argument runs when the validation fails
      (val) => {
        // Remove special characters before checking length requirements
        const strippedVal = val.replace(MODULR_SPECIAL_CHARS, '');

        return {
          message:
            strippedVal.length < 6
              ? 'Payment reference must be at least 6 alphanumeric characters'
              : new Set(strippedVal).size === 1
                ? 'Payment reference cannot contain all the same alphanumeric characters'
                : !/^[a-zA-Z0-9-.&/\s]*$/.test(strippedVal)
                  ? 'Payment reference can only contain letters, numbers, -, ., &, / and spaces'
                  : currency !== Currency.GBP && val.length > 140
                    ? 'Payment reference must not exceed 140 alphanumeric characters'
                    : 'Payment reference must not exceed 18 alphanumeric characters',
        };
      },
    ),
  });

export type BeneficiaryDetails = {
  name: ComponentProps<typeof BeneficiaryCard>['name'];
  currency: ComponentProps<typeof BeneficiaryCard>['currency'];
  accountDetails: ComponentProps<typeof BeneficiaryCard>['accountDetails'];
  latestPayeeCheck: ComponentProps<typeof BeneficiaryCard>['latestPayeeCheck'];
};

type ExternalPaymentFormValues = z.infer<ReturnType<typeof createFormSchema>>;

type ExternalPaymentFormProps = {
  accounts: AccountView[];
  isLoading: boolean;
  beneficiaryDetails: BeneficiaryDetails;
  onCancelTransfer: () => void;
  onSubmit: (values: ExternalPaymentFormValues) => void;
};

export const ExternalPaymentForm = ({
  accounts,
  isLoading,
  beneficiaryDetails,
  onCancelTransfer,
  onSubmit,
}: ExternalPaymentFormProps) => {
  const { name, currency, accountDetails, latestPayeeCheck } = beneficiaryDetails;

  const availableSourceAccounts = accounts.filter((a) => a.currency === currency).filter(canInitiatePayments);
  const defaultSourceAccount = availableSourceAccounts[0]?.id;
  const [selectedSourceAccountId, setSelectedSourceAccountId] = useState(defaultSourceAccount);

  const selectedSourceAccount = availableSourceAccounts.find((a) => a.id === selectedSourceAccountId);

  const hasSourceAccounts = availableSourceAccounts.length > 0;

  const form = useForm<ExternalPaymentFormValues>({
    resolver: zodResolver(createFormSchema(currency, selectedSourceAccount?.balance)),
    defaultValues: {
      sourceAccountId: selectedSourceAccountId,
      paymentReference: '',
    },
    mode: 'onTouched',
  });

  const { errors, isValid } = form.formState;

  useEffect(() => {
    // Triggers validation on amount field when source account is changed
    // Not running in on undefined prevents running it on initial render
    if (form.getValues('amount') !== undefined && selectedSourceAccountId) {
      form.trigger('amount');
    }
  }, [form, selectedSourceAccountId]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((data) => onSubmit(data))} className="w-full">
        <Stack align="stretch" space={3}>
          <Stack
            className={cn(
              'rounded-lg border border-seapoint-gray-border focus-within:outline-none focus-within:ring-2 focus-within:ring-seapoint-yellow focus-within:ring-offset-2',
              {
                'border-seapoint-red': !!errors.amount || !!errors.sourceAccountId,
              },
            )}
          >
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => {
                const currencySymbol = getCurrencySymbol(currency);

                return (
                  <FormItem>
                    <VisuallyHidden>
                      <FormLabel>Amount to be transferred</FormLabel>
                    </VisuallyHidden>

                    <FormControl>
                      <Input
                        required
                        leftIcon={<p className="text-base font-medium text-seapoint-gray">{currency}</p>}
                        type="text"
                        placeholder={`${currencySymbol}0.00`}
                        rightAlign
                        className={cn(
                          'rounded-b-none rounded-t-lg border-0 bg-white py-2 pl-9 pr-4 text-2xl [appearance:textfield] placeholder:text-seapoint-gray focus-visible:ring-0 focus-visible:ring-offset-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                        )}
                        {...field}
                        value={field.value ? `${currencySymbol}${field.value}` : ''}
                        onChange={(e) => {
                          // Remove currency symbol and non-numeric chars, then convert to number
                          const numericValue = e.target.value.replace(currencySymbol, '').replace(/[^\d.]/g, '');

                          field.onChange(numericValue);
                        }}
                        onFocus={(e) => {
                          if (e.target.value === currencySymbol + '0') {
                            e.target.value = currencySymbol;
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />

            {hasSourceAccounts && (
              <FormField
                control={form.control}
                name="sourceAccountId"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <VisuallyHidden>
                        <FormLabel htmlFor="from-account-select">From Account</FormLabel>
                      </VisuallyHidden>

                      <FormControl>
                        <Select
                          id="from-account-select"
                          aria-labelledby="from-account-label"
                          required
                          placeholder={
                            <AccountSelectItem
                              balance={availableSourceAccounts[0].balance}
                              currency={availableSourceAccounts[0].currency}
                              institutionLogo={availableSourceAccounts[0].institution?.logo}
                              name={availableSourceAccounts[0].name}
                              fspAdapter={availableSourceAccounts[0].fspAdapter}
                            />
                          }
                          onValueChange={(value) => {
                            const selectedAccount = availableSourceAccounts.find((a) => a.id === value);

                            field.onChange(value);

                            if (selectedAccount) {
                              setSelectedSourceAccountId(selectedAccount.id);
                            }
                          }}
                          defaultValue={selectedSourceAccountId}
                          triggerClassName={cn(
                            'py-1.5 rounded-b-lg rounded-t-none [&_.account-logo]:w-4 [&_.account-logo]:h-4 [&_.account-type]:hidden justify-start gap-2 bg-seapoint-gray-background-modal focus:ring-0 focus:ring-offset-0',
                          )}
                        >
                          {availableSourceAccounts.map((account) => {
                            const accountName = getBankAccountName({
                              displayName: account.displayName,
                              institutionName: account.institution?.name,
                            });

                            return (
                              <Select.Item key={account.id} value={account.id} className="py-3">
                                <AccountSelectItem
                                  balance={account.balance}
                                  currency={account.currency}
                                  institutionLogo={account.institution?.logo}
                                  name={accountName}
                                  fspAdapter={account.fspAdapter}
                                />
                              </Select.Item>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </FormItem>
                  );
                }}
              />
            )}

            {!hasSourceAccounts && (
              <CurrencyAccountNotFound
                currency={currency}
                // TODO: not sure what is the flow for this
                onOpenAccount={() => {}}
                onCancelTransfer={onCancelTransfer}
              />
            )}
          </Stack>

          <BeneficiaryCard
            name={name}
            currency={currency}
            accountDetails={accountDetails}
            latestPayeeCheck={latestPayeeCheck}
          />

          <FormField
            control={form.control}
            name="paymentReference"
            render={({ field }) => {
              return (
                <FormItem
                  className={cn(
                    'rounded-lg border border-seapoint-gray-border bg-white px-4 py-3 focus-within:outline-none focus-within:ring-2 focus-within:ring-seapoint-yellow focus-within:ring-offset-2',
                    { 'border-seapoint-red': !!errors.paymentReference },
                  )}
                >
                  <FormLabel className="text-xs font-medium text-seapoint-gray">Reference</FormLabel>

                  <FormControl>
                    <input
                      type="text"
                      placeholder="Personal transfer"
                      className="flex min-h-8 w-full bg-transparent text-base font-medium text-black placeholder:text-outline focus-visible:outline-none"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <Stack align="stretch" space={1}>
            {errors.amount && <p className="text-sm font-medium text-seapoint-red">{errors.amount.message}</p>}

            {errors.sourceAccountId && (
              <p className="text-sm font-medium text-seapoint-red">{errors.sourceAccountId.message}</p>
            )}

            {errors.paymentReference && (
              <p className="text-sm font-medium text-seapoint-red">{errors.paymentReference.message}</p>
            )}
          </Stack>

          <Button
            variant="default"
            size="lg"
            type="submit"
            disabled={!isValid || !hasSourceAccounts}
            isLoading={isLoading}
          >
            Continue
          </Button>
        </Stack>
      </form>
    </Form>
  );
};
